import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { DialogComponent } from 'ReusableComponents/Modal/ModalComponent';
import { Dayjs } from 'dayjs';
import { CarType, CourierJson } from 'types/bundleTypes';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import {
  CourierLists,
  RoleFilterType,
  SortingIcon,
} from './courierModalComponents';
import './CourierModals.scss';
import clsx from 'clsx';
import { InputComponent } from '../../../ReusableComponents/Input/InputComponent';
import { BiSearch } from 'react-icons/bi';
import { RadioComponent } from '../../../ReusableComponents/Radio/RadioComponent';
import { CheckboxComponent } from '../../../ReusableComponents/Checkbox/CheckboxComponent';
import TablePagination from '@material-ui/core/TablePagination';
import { RoleIcons } from '../smallerComponents';
import { TooltipComponent } from '../../../ReusableComponents/Tooltip/TooltipComponent';
import { IShift } from '../shiftApi';
import { mapCourierIdsToShiftStatus, ShiftStatusType } from '../utils';

enum SortType {
  ASCENDING,
  DESCENDING,
  NEUTRAL,
}

const CourierContactModal: FC<{
  date: Dayjs;
  courierShiftMap: { [courierId: number]: IShift };
  couriers: CourierJson[];
  showModal: boolean;
  setShowModal: (open: boolean) => void;
}> = ({ date, couriers, showModal, setShowModal, courierShiftMap }) => {
  const [courierSearch, setCourierSearch] = useState<string>('');

  const [couriersToDisplay, setCouriersToDisplay] = useState<CourierLists>({});
  const [selectedCouriers, setSelectedCouriers] = useState<Set<number>>(
    new Set()
  );
  const [checkedValue, setCheckedValue] = useState<RoleFilterType>(
    RoleFilterType.ALL
  );

  const [selectedAvailability, setSelectedAvailability] = useState<
    Set<ShiftStatusType>
  >(new Set());

  const setCourierSearchCallback = (e: ChangeEvent<HTMLInputElement>) =>
    setCourierSearch(e.currentTarget.value);

  const handleCopyNumbers = () => {
    const numbers = couriers
      .filter(courier => selectedCouriers.has(courier.id))
      .map(courier => courier.phoneNumber)
      .join(' ');

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(numbers).catch(err => console.error(err));
    }
  };

  const handleAvailabilityChange = (shiftAvailability: ShiftStatusType) => {
    setSelectedAvailability(prevState => {
      const prevStateCopy = new Set(prevState);
      prevStateCopy.has(shiftAvailability)
        ? prevStateCopy.delete(shiftAvailability)
        : prevStateCopy.add(shiftAvailability);
      return prevStateCopy;
    });
  };

  useEffect(() => {
    const initialCouriers = [...couriers];
    let filteredCouriers = [];

    const { booked, requested, declined, canceled, dispatchDeclined } =
      mapCourierIdsToShiftStatus(Object.values(courierShiftMap));

    if (selectedAvailability.has(ShiftStatusType.BOOKED)) {
      filteredCouriers = [
        ...filteredCouriers,
        ...initialCouriers
          .filter(courier => booked.includes(courier.id))
          .map(courier => courier),
      ];
    }

    if (selectedAvailability.has(ShiftStatusType.DECLINED)) {
      filteredCouriers = [
        ...filteredCouriers,
        ...initialCouriers
          .filter(courier => declined.includes(courier.id))
          .map(courier => courier),
      ];
    }

    if (selectedAvailability.has(ShiftStatusType.DISPATCH_DECLINED)) {
      filteredCouriers = [
        ...filteredCouriers,
        ...initialCouriers
          .filter(courier => dispatchDeclined.includes(courier.id))
          .map(courier => courier),
      ];
    }

    if (selectedAvailability.has(ShiftStatusType.REQUESTED)) {
      filteredCouriers = [
        ...filteredCouriers,
        ...initialCouriers
          .filter(courier => requested.includes(courier.id))
          .map(courier => courier),
      ];
    }

    if (selectedAvailability.has(ShiftStatusType.CANCELED)) {
      filteredCouriers = [
        ...filteredCouriers,
        ...initialCouriers
          .filter(courier => canceled.includes(courier.id))
          .map(courier => courier),
      ];
    }

    filteredCouriers =
      selectedAvailability.size === 0 ? initialCouriers : filteredCouriers;

    if (courierSearch) {
      filteredCouriers = filteredCouriers.filter(courier =>
        courier.fullName.toLowerCase().includes(courierSearch.toLowerCase())
      );
    }

    if (checkedValue === RoleFilterType.COURIER) {
      setCouriersToDisplay({
        Courier: [
          ...filteredCouriers
            .filter(courier => courier.isCourier)
            .map(courier => courier),
        ],
      });
      return;
    }

    if (checkedValue === RoleFilterType.SORTER) {
      setCouriersToDisplay({
        Sorter: [
          ...filteredCouriers
            .filter(courier => courier.isSorter)
            .map(courier => courier),
        ],
      });
      return;
    }

    setCouriersToDisplay({
      All: filteredCouriers,
    });
  }, [
    checkedValue,
    courierSearch,
    courierShiftMap,
    couriers,
    selectedAvailability,
  ]);

  return (
    <DialogComponent
      className="courierPickerModal"
      open={showModal}
      setOpen={setShowModal}
    >
      <div className="courierPickerModal__hero">
        <h1>Contact details</h1>
        <p>{date.format('DD.MM.YYYY')}</p>
      </div>
      <div className="courierFilterSection">
        <div className="courierFilterSection__top">
          <p>Search:</p>
          <div className="courierFilterSection__top__search">
            <InputComponent
              className="courierFilterSection__top__search__input"
              margin="none"
              id="shift-modal-couriersearch"
              label="Search courier"
              name="courier-search"
              value={courierSearch}
              onChange={setCourierSearchCallback}
              variant="outlined"
            />
            <ButtonComponent
              className="courierFilterSection__top__search__button"
              style={{ backgroundColor: '#666666' }}
            >
              <BiSearch />
            </ButtonComponent>
          </div>
        </div>
        <div className="courierFilterSection__bottom">
          <div className="courierFilterSection__bottom__workTypeFilter">
            <p className="courierFilterSection__bottom__workTypeFilter__description">
              Filter job type:
            </p>
            <div className="courierFilterSection__bottom__workTypeFilter__radios">
              <RadioComponent
                id="courierPicker-1"
                name="courierPicker-radio"
                label={{ node: 'All' }}
                checked={checkedValue === RoleFilterType.ALL}
                value={RoleFilterType.ALL}
                onChange={() => setCheckedValue(RoleFilterType.ALL)}
              />
              <RadioComponent
                id="courierPicker-2"
                name="courierPicker-radio"
                label={{ node: 'Courier' }}
                checked={checkedValue === RoleFilterType.COURIER}
                value={RoleFilterType.COURIER}
                onChange={() => setCheckedValue(RoleFilterType.COURIER)}
              />
              <RadioComponent
                id="courierPicker-3"
                name="courierPicker-radio"
                label={{ node: 'Sorter' }}
                checked={checkedValue === RoleFilterType.SORTER}
                value={RoleFilterType.SORTER}
                onChange={() => setCheckedValue(RoleFilterType.SORTER)}
              />
            </div>
          </div>
          <div className="courierFilterSection__bottom__availabilityFilter">
            <p className="courierFilterSection__bottom__availabilityFilter__description">
              Filter availability:
            </p>
            <div className="courierFilterSection__bottom__availabilityFilter__checkboxes">
              <CheckboxComponent
                key="booked-check"
                checkBoxClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__checkbox"
                labelClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__label"
                checked={selectedAvailability.has(ShiftStatusType.BOOKED)}
                name=""
                label="Booked"
                handleChange={() =>
                  handleAvailabilityChange(ShiftStatusType.BOOKED)
                }
              />
              <CheckboxComponent
                key="requested-to-work-check"
                checkBoxClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__checkbox"
                labelClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__label"
                checked={selectedAvailability.has(ShiftStatusType.REQUESTED)}
                name=""
                label="Requested to work"
                handleChange={() =>
                  handleAvailabilityChange(ShiftStatusType.REQUESTED)
                }
              />
              <CheckboxComponent
                key="declined-check"
                checkBoxClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__checkbox"
                labelClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__label"
                checked={selectedAvailability.has(ShiftStatusType.DECLINED)}
                name=""
                label="Declined"
                handleChange={() =>
                  handleAvailabilityChange(ShiftStatusType.DECLINED)
                }
              />
              <CheckboxComponent
                key="canceled-check"
                checkBoxClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__checkbox"
                labelClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__label"
                checked={selectedAvailability.has(ShiftStatusType.CANCELED)}
                name=""
                label="Canceled"
                handleChange={() =>
                  handleAvailabilityChange(ShiftStatusType.CANCELED)
                }
              />
              <CheckboxComponent
                key="dispatch-declined-check"
                checkBoxClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__checkbox"
                labelClassName="courierFilterSection__bottom__availabilityFilter__checkboxes__label"
                checked={selectedAvailability.has(
                  ShiftStatusType.DISPATCH_DECLINED
                )}
                name=""
                label="Dispatch declined"
                handleChange={() =>
                  handleAvailabilityChange(ShiftStatusType.DISPATCH_DECLINED)
                }
              />
            </div>
          </div>
        </div>
      </div>
      {Object.entries(couriersToDisplay).map(([groupName, groupCouriers]) => (
        <CourierContactsSection
          key={groupName}
          groupName={groupName}
          couriers={groupCouriers}
          selectedCouriers={selectedCouriers}
          setSelectedCouriers={setSelectedCouriers}
        />
      ))}
      <div className="courierPickerModal__buttons">
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__confirm',
            'sharedButton'
          )}
          disabled={selectedCouriers.size === 0}
          text={
            selectedCouriers.size > 0
              ? `Copy numbers (${selectedCouriers.size})`
              : 'Copy numbers'
          }
          onClick={handleCopyNumbers}
          customColor={{ background: '#443df6', color: 'white' }}
        />
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__cancel',
            'sharedButton'
          )}
          text="Close"
          onClick={() => setShowModal(false)}
        />
      </div>
    </DialogComponent>
  );
};

const CourierContactsSection: FC<{
  groupName: string;
  couriers: CourierJson[];
  selectedCouriers: Set<number>;
  setSelectedCouriers: Dispatch<SetStateAction<Set<number>>> | null;
}> = ({ groupName, couriers, selectedCouriers, setSelectedCouriers }) => {
  const [couriersToDisplay, setCouriersToDisplay] =
    useState<CourierJson[]>(couriers);

  const [sortBy, setSortBy] = useState<SortType>(SortType.NEUTRAL);
  const [page, setPage] = useState<number>(0);

  const itemsPerPage = 45;
  const pagination = couriersToDisplay?.length ? (
    <TablePagination
      count={couriersToDisplay?.length}
      page={page}
      onPageChange={(_, value: number) => setPage(value)}
      rowsPerPage={itemsPerPage}
      rowsPerPageOptions={[]}
    />
  ) : null;

  const couriersPerPage: CourierJson[] = couriersToDisplay.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage
  );

  const couriersChunks: Record<number, CourierJson[]> = couriersPerPage.reduce(
    (resultArray, item, index) => {
      const chunkIndex = Math.floor(index / 15);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    },
    []
  );

  const changeSortBy = () => {
    const newOrder =
      sortBy === SortType.ASCENDING ? SortType.DESCENDING : SortType.ASCENDING;

    const sortedCouriers = [...couriersToDisplay];
    sortedCouriers.sort((a: CourierJson, b: CourierJson) =>
      newOrder === SortType.ASCENDING
        ? a.fullName.localeCompare(b.fullName)
        : -a.fullName.localeCompare(b.fullName)
    );

    setSortBy(newOrder);
    setCouriersToDisplay(sortedCouriers);
  };

  const handleSelectAllCouriers = (e: ChangeEvent<HTMLInputElement>) => {
    const isSelectAllChecked = e.currentTarget.checked;
    setSelectedCouriers(prevSelectedIds => {
      const selectedIdsCopy = new Set(prevSelectedIds);
      isSelectAllChecked
        ? couriersToDisplay.forEach(courier => selectedIdsCopy.add(courier.id))
        : couriersToDisplay.forEach(courier =>
            selectedIdsCopy.delete(courier.id)
          );
      return selectedIdsCopy;
    });
  };

  const allCouriersSelected =
    selectedCouriers.size > 0 &&
    couriersToDisplay.every(courier => selectedCouriers.has(courier.id));

  useEffect(() => {
    setPage(0);
    setCouriersToDisplay(couriers);
  }, [couriers]);

  return (
    <div className="courierContactsSection">
      <p>{groupName}</p>
      <div className="courierContactsSection__table">
        {Object.entries(couriersChunks).map(
          ([courierArray, couriersChunk], index) => (
            <ContactsColumn
              key={`contacts-column-${index}`}
              hideHeaderCheckbox={index > 0}
              couriers={couriersChunk}
              setSelectedCouriers={setSelectedCouriers}
              selectedCouriers={selectedCouriers}
              changeSortBy={changeSortBy}
              sortBy={sortBy}
              handleSelectAllCouriers={handleSelectAllCouriers}
              allCouriersSelected={allCouriersSelected}
            />
          )
        )}
      </div>
      <div>{pagination}</div>
    </div>
  );
};

const ContactsColumn: FC<{
  couriers: CourierJson[];
  selectedCouriers: Set<number>;
  setSelectedCouriers: Dispatch<SetStateAction<Set<number>>>;
  allCouriersSelected: boolean;
  handleSelectAllCouriers: (e: ChangeEvent<HTMLInputElement>) => void;
  changeSortBy: () => void;
  sortBy: SortType;
  hideHeaderCheckbox: boolean;
}> = ({
  couriers,
  selectedCouriers,
  setSelectedCouriers,
  allCouriersSelected,
  handleSelectAllCouriers,
  changeSortBy,
  sortBy,
  hideHeaderCheckbox,
}) => {
  const toggleSelectedCourier = (courierId: number) => {
    setSelectedCouriers(currentSelected => {
      const selectedCouriersCopy = new Set(currentSelected);
      selectedCouriersCopy.has(courierId)
        ? selectedCouriersCopy.delete(courierId)
        : selectedCouriersCopy.add(courierId);
      return selectedCouriersCopy;
    });
  };

  return (
    <div>
      <div className="courierContactsSection__table__header">
        {hideHeaderCheckbox ? (
          <div />
        ) : (
          <CheckboxComponent
            checked={allCouriersSelected}
            handleChange={handleSelectAllCouriers}
            name="select-all-couriers"
          />
        )}
        <p>Roles</p>
        <div
          className="courierContactsSection__table__header__nameSort"
          onClick={() => changeSortBy()}
        >
          <span>Names</span>
          <SortingIcon sortType={sortBy ?? SortType.NEUTRAL} />
        </div>
      </div>
      {couriers.map(courier => (
        <div
          className="courierContactsSection__table__row"
          key={courier.id}
          onClick={() => toggleSelectedCourier(courier.id)}
        >
          <CheckboxComponent
            checked={selectedCouriers.has(courier.id)}
            name={`courier_${courier.id}`}
          />
          <RoleIcons
            vehicleType={courier.isCourier && courier.travelMode}
            isElectric={courier.carType === CarType.ELECTRIC}
            isScanner={courier.isSorter}
          />
          <TooltipComponent
            className="courierContactsSection__nameTooltip"
            id={courier.fullName}
            title={`${courier.fullName}: ${courier.phoneNumber}`}
            placement="top-start"
          >
            <span>{courier.fullName}</span>
          </TooltipComponent>
        </div>
      ))}
    </div>
  );
};

export default CourierContactModal;
